<template>
  <v-card class="runners-box" flat>
    <header class="runners-header">
      <div class="runners-header-main">
        <div class="runners-eyebrow">Etenemiset</div>
        <h3 class="runners-title">Nopeimmat etenijät</h3>
        <div class="runners-subtitle">
          Top 5 keskimääräisen etenemisajan mukaan
        </div>
      </div>

      <div class="runners-chips">
        <button
          v-for="base in bases"
          :key="`base-${base.value}`"
          type="button"
          class="base-chip"
          :class="{ active: selectedBase === base.value }"
          @click="selectedBase = base.value"
        >
          {{ base.text }}
        </button>
      </div>
    </header>

    <div class="runners-body">
      <div v-if="!canFetch" class="runners-empty">
        Valitse joukkue ja kausi.
      </div>

      <div v-else-if="loading" class="runners-loading">
        <div v-for="i in 5" :key="i" class="skeleton-row" />
      </div>

      <v-alert v-else-if="error" type="error" dense outlined class="runners-error">
        {{ error }}
      </v-alert>

      <div v-else-if="!topRunners.length" class="runners-empty">
        Ei etenemisdataa valitulla pesänvälillä.
      </div>

      <table v-else class="runners-table">
        <thead>
          <tr>
            <th class="col-rank">#</th>
            <th class="col-player">Pelaaja</th>
            <th class="col-time">Ka.</th>
            <th class="col-count">Määrä</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(runner, index) in topRunners"
            :key="`runner-${runner.player_id}`"
          >
            <td class="col-rank">{{ index + 1 }}</td>
            <td class="col-player">
              <div class="player-cell">
                <div class="player-avatar">
                  <img
                    v-if="runner.picture_url"
                    :src="runner.picture_url"
                    :alt="runner.player_name"
                  />
                  <span v-else class="player-initials">
                    {{ initials(runner.player_name) }}
                  </span>
                </div>
                <div class="player-name">{{ runner.player_name }}</div>
              </div>
            </td>
            <td class="col-time">{{ formatTime(runner.avg_eteneminen_time) }}</td>
            <td class="col-count">
              <span
                class="run-count"
                :class="{ 'run-count-low': runner.num_of_runs < 3 }"
              >
                {{ runner.num_of_runs }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import ax from '@/utils/axios/axios'

export default {
  name: 'TopRunnersBox',
  props: {
    leagueId: {
      type: Number,
      default: null
    },
    leagueClubId: {
      type: Number,
      default: null
    },
    seasonId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    selectedBase: 1,
    bases: [
      { value: 1, text: '1 → 2' },
      { value: 2, text: '2 → 3' },
      { value: 3, text: '3 → K' }
    ],
    runs: [],
    loading: false,
    error: null,
    requestId: 0
  }),
  computed: {
    ...mapGetters('admin', [
      'season_by_id'
    ]),
    season() {
      return this.seasonId ? this.season_by_id(this.seasonId) : null
    },
    seasonRange() {
      if(!this.season || !this.season.starttime || !this.season.endtime) return null

      const start = new Date(this.season.starttime).getTime()
      const end = new Date(this.season.endtime).getTime()
      if(Number.isNaN(start) || Number.isNaN(end)) return null

      return { start, end }
    },
    canFetch() {
      return Boolean(
        this.leagueId &&
        this.leagueClubId &&
        this.seasonId &&
        this.seasonRange
      )
    },
    requestKey() {
      return [
        this.leagueId,
        this.leagueClubId,
        this.seasonId,
        this.selectedBase
      ].join(':')
    },
    topRunners() {
      return this.runs.slice(0, 5)
    }
  },
  watch: {
    requestKey: {
      immediate: true,
      handler() {
        this.refresh()
      }
    }
  },
  methods: {
    refresh() {
      if(!this.canFetch) {
        this.runs = []
        this.error = null
        this.loading = false
        return
      }

      const requestId = this.requestId + 1
      this.requestId = requestId
      this.loading = true
      this.error = null

      const { start, end } = this.seasonRange
      ax.get(`/league/${this.leagueId}/times`, {
        params: {
          base: this.selectedBase,
          start,
          end,
          club_ids: `[${this.leagueClubId}]`,
          is_play: false
        }
      })
        .then(res => {
          if(requestId !== this.requestId) return
          this.runs = Array.isArray(res.data) ? res.data : []
        })
        .catch(err => {
          if(requestId !== this.requestId) return
          this.runs = []
          this.error = err.response?.data?.error || 'Etenemisten haku epäonnistui'
        })
        .finally(() => {
          if(requestId === this.requestId) this.loading = false
        })
    },
    initials(name) {
      if(!name) return ''
      return name
        .split(/\s+/)
        .filter(Boolean)
        .map(part => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },
    formatTime(value) {
      if(value === null || value === undefined) return '–'
      const num = Number(value)
      if(!Number.isFinite(num)) return '–'
      return `${num.toFixed(2)} s`
    }
  }
}
</script>

<style scoped>
.runners-box {
  margin-top: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.runners-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

.runners-header-main {
  min-width: 0;
}

.runners-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
}

.runners-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.runners-subtitle {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
}

.runners-chips {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.base-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}

.base-chip:hover {
  background: #f9fafb;
}

.base-chip.active {
  background: #111827;
  border-color: #111827;
  color: #ffffff;
}

.runners-body {
  padding: 4px 0;
}

.runners-empty {
  padding: 32px 24px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
}

.runners-loading {
  padding: 12px 24px 16px;
}

.skeleton-row {
  height: 44px;
  margin: 6px 0;
  border-radius: 6px;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.runners-error {
  margin: 16px 24px;
}

.runners-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.runners-table thead th {
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  text-align: left;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.runners-table thead th.col-rank,
.runners-table thead th.col-time,
.runners-table thead th.col-count {
  text-align: center;
}

.runners-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 120ms ease;
}

.runners-table tbody tr:last-child {
  border-bottom: none;
}

.runners-table tbody tr:hover {
  background: #fafafa;
}

.runners-table td {
  padding: 12px 16px;
  vertical-align: middle;
  color: #111827;
}

.col-rank {
  width: 48px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #6b7280;
  text-align: center;
}

.col-time,
.col-count {
  width: 100px;
  font-variant-numeric: tabular-nums;
  text-align: center;
  font-weight: 500;
}

.col-time {
  font-weight: 700;
}

.run-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ecfdf5;
  color: #047857;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.run-count-low {
  background: #fef2f2;
  color: #b91c1c;
}

.col-player {
  min-width: 0;
}

.player-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.player-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.player-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.player-initials {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.player-name {
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
